import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, Button, Box, Typography, Autocomplete, TextField } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import SupplierExtractedFields from './SupplierExtractedFields';
import './SupplierPage.css';
import './SupplierGridView.css';
import axiosInstance from './axiosInstance';

const normaliseSupplierKey = (supplier) => {
  if (!supplier) return '';
  const raw = supplier.supplier_id;
  if (raw === null || raw === undefined) return '';
  return String(raw).trim().toLowerCase();
};

const SupplierPage = ({
  detailedData, editableData, tempEditableData, setTempEditableData, sites, suppliers, nominalCodes, docStatuses, 
  currentImageIndex, isEdited, zoomEnabled, transformWrapperRef, handleBackClick, handleFieldChange, 
  handleFieldBlur, handlePreviousImage, handleNextImage, handleZoomToggle, handleSaveChanges, 
  otherDocuments, handleCardClick, getLocationOptionLabel, 
  getSupplierOptionLabel, getNominalCodeOptionLabel, refetchData, imageName, advanceToNextDocument,
  isMappingMode = false,
  isReadOnly = false
}) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [supplierOptions, setSupplierOptions] = useState(() => (Array.isArray(suppliers) ? suppliers : []));
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);

  const supplierFilterOptions = useMemo(() => supplierOptions, [supplierOptions]);

  const mergeSupplierOptions = useCallback((incomingSuppliers = []) => {
    setSupplierOptions((prev) => {
      const map = new Map();
      [...(Array.isArray(prev) ? prev : []), ...incomingSuppliers].forEach((supplier) => {
        const key = normaliseSupplierKey(supplier);
        if (!key || map.has(key)) return;
        map.set(key, supplier);
      });
      return Array.from(map.values()).sort((a, b) => {
        const nameA = (a?.name || '').toLowerCase();
        const nameB = (b?.name || '').toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    });
  }, []);


  useEffect(() => {
    if (Array.isArray(suppliers) && suppliers.length > 0) {
      mergeSupplierOptions(suppliers);
    }
  }, [suppliers, mergeSupplierOptions]);

  useEffect(() => {
    let cancelled = false;

    const fetchAllSuppliers = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/supplier');
        if (cancelled) return;
        const payload = Array.isArray(response?.data?.data) ? response.data.data : [];
        mergeSupplierOptions(payload);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
      }
    };

    fetchAllSuppliers();

    return () => {
      cancelled = true;
    };
  }, [mergeSupplierOptions]);

  useEffect(() => {
    if (!Array.isArray(supplierOptions) || supplierOptions.length === 0) {
      setSelectedSupplierOption(null);
      return;
    }

    const activeSupplierId = tempEditableData?.supplier_id || detailedData?.supplier_id;
    if (!activeSupplierId) {
      setSelectedSupplierOption(null);
      return;
    }

    const key = String(activeSupplierId).trim().toLowerCase();
    const match = supplierOptions.find((supplier) => normaliseSupplierKey(supplier) === key);
    setSelectedSupplierOption(match || null);
  }, [supplierOptions, detailedData?.supplier_id, tempEditableData?.supplier_id]);

  const validateAllFields = useCallback(() => {
    let errors = {};
    const requiredFields = ['supplier_id', 'name'];
    requiredFields.forEach(field => {
      if (!tempEditableData[field]) {
        errors[field] = 'This field is required';
      }
    });
    setValidationErrors(errors);
  }, [tempEditableData]);

  useEffect(() => {
    validateAllFields();
  }, [tempEditableData, validateAllFields]);

  const filteredOtherDocuments = useMemo(() => {
    if (!Array.isArray(otherDocuments)) return [];
    if (!selectedSupplierOption) return otherDocuments;
    const key = normaliseSupplierKey(selectedSupplierOption);
    if (!key) return otherDocuments;
    return otherDocuments.filter((doc) => normaliseSupplierKey(doc) === key);
  }, [otherDocuments, selectedSupplierOption]);

  const handleSupplierFilterChange = useCallback((event, newValue) => {
    setSelectedSupplierOption(newValue || null);

    if (!newValue) {
      return;
    }

    const selectedId = newValue.supplier_id;
    const matchingDocument =
      (detailedData && detailedData.supplier_id === selectedId ? detailedData : null) ||
      filteredOtherDocuments.find((doc) => doc.supplier_id === selectedId) ||
      (Array.isArray(otherDocuments) ? otherDocuments.find((doc) => doc.supplier_id === selectedId) : null);

    if (matchingDocument) {
      handleCardClick(matchingDocument);
    } else {
      handleCardClick({ supplier_id: selectedId, name: newValue.name });
    }
  }, [detailedData, otherDocuments, filteredOtherDocuments, handleCardClick]);

  const containerStyle = isMappingMode
    ? { paddingLeft: 0, paddingRight: 0 }
    : { paddingLeft: '200px', paddingRight: '200px' };

  const filterGridSizes = isMappingMode ? { xs: 12 } : { xs: 12, md: 6 };
  const formGridSizes = isMappingMode
    ? { xs: 12 }
    : { xs: 12, md: 8, lg: 6 };

  return (
    <div className="supplier-page" style={containerStyle}>
      <Grid container spacing={2} style={{ marginBottom: '10px' }} alignItems="center">
        <Grid item {...filterGridSizes} style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBackClick}>
            Return to Grid
          </Button>
        </Grid>
        {!isMappingMode && (
          <Grid item {...filterGridSizes}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: { xs: 'stretch', sm: 'flex-end' },
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: { xs: '100%', sm: 240 },
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Filter by Supplier
                </Typography>
                <Autocomplete
                  options={supplierFilterOptions}
                  value={selectedSupplierOption}
                  onChange={handleSupplierFilterChange}
                  getOptionLabel={getSupplierOptionLabel || ((option) => (option?.name ? option.name : ''))}
                  renderOption={(props, option) => (
                    <li {...props} key={option.supplier_id}>
                      <span>{getSupplierOptionLabel ? getSupplierOptionLabel(option) : option.name}</span>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option?.supplier_id === value?.supplier_id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="All Suppliers"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      <Grid container justifyContent={isMappingMode ? 'center' : 'flex-start'}>
        <Grid item {...formGridSizes} style={{ width: '100%' }}>
          <SupplierExtractedFields
            tempEditableData={tempEditableData}
            setTempEditableData={setTempEditableData}
            suppliers={supplierFilterOptions}
            handleFieldChange={handleFieldChange}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            isEdited={isEdited}
            handleSaveChanges={handleSaveChanges}
            getSupplierOptionLabel={getSupplierOptionLabel}
            refetchData={refetchData}
            isReadOnly={isReadOnly}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default SupplierPage;
